import { isValid, getYear } from "date-fns";
import type { BannerSwap } from "../../utils/@types";

/** Parse Firestore Timestamp / ISO / epoch values from API JSON into a Date. */
export function toBannerSwapDate(raw: unknown): Date {
  if (raw == null || raw === "") {
    return new Date(NaN);
  }
  if (raw instanceof Date) {
    return isValid(raw) ? new Date(raw.getTime()) : new Date(NaN);
  }
  if (typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    const sec =
      typeof o.seconds === "number"
        ? o.seconds
        : typeof o._seconds === "number"
          ? o._seconds
          : undefined;
    const nanos =
      typeof o.nanoseconds === "number"
        ? o.nanoseconds
        : typeof o._nanoseconds === "number"
          ? o._nanoseconds
          : 0;
    if (sec !== undefined) {
      return new Date(sec * 1000 + nanos / 1e6);
    }
    if (typeof o.toDate === "function") {
      try {
        const d = (o as { toDate: () => Date }).toDate();
        return isValid(d) ? d : new Date(NaN);
      } catch {
        return new Date(NaN);
      }
    }
  }
  if (typeof raw === "string" || typeof raw === "number") {
    const d = new Date(raw);
    return isValid(d) ? d : new Date(NaN);
  }
  return new Date(NaN);
}

/**
 * Canonical permanent-end test, shared by client UI + overlap logic.
 * Matches the server (`isPermanentEndDate`: year >= 9990). Permanent swaps are
 * stamped as 9999; using the same threshold avoids client/server drift.
 */
export function isPermanentSwapEnd(date: Date): boolean {
  return isValid(date) && getYear(date) >= 9990;
}

export function formatSwapDateCell(raw: unknown): string {
  const d = toBannerSwapDate(raw);
  if (!isValid(d)) {
    return "—";
  }
  if (isPermanentSwapEnd(d)) {
    return "Permanent";
  }
  try {
    return d.toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

/**
 * Per-channel overlap check for editing a running swap's end date.
 * Server parity: `findRunningEndOverlapPeers` in `bannerSwapOverlapService.ts`.
 *
 * A peer only conflicts when it shares a channel whose effective windows
 * actually intersect (half-open). Peers this swap delayed are excluded. The
 * proposed `end` applies to every channel; per-channel start uses the edited
 * swap's own `channelSchedules` override when present.
 */
export function scheduleOverlapsPeers(
  channelIDs: string[],
  start: Date,
  end: Date,
  excludeSwapUID: string,
  queue: BannerSwap[],
  channelSchedules?: BannerSwap["channelSchedules"]
): string[] {
  const overlaps: string[] = [];
  const e = end.getTime();
  for (const existing of queue) {
    if (existing.swapUID === excludeSwapUID) {
      continue;
    }
    if (!["scheduled", "running"].includes(existing.status)) {
      continue;
    }
    // Peers this swap delayed are reconciled server-side, so they are not a real
    // conflict for this blocker (mirrors patchBannerSwapRunningEndDate).
    const delayedByThisSwap = existing.channelSchedules
      ? Object.values(existing.channelSchedules).some(
          (sched) => sched.delayedBySwapUID === excludeSwapUID
        )
      : false;
    if (delayedByThisSwap) {
      continue;
    }
    for (const channelId of channelIDs) {
      if (!existing.channelIDs.includes(channelId)) {
        continue;
      }
      const ownOverride = channelSchedules?.[channelId];
      const ownStart = ownOverride
        ? toBannerSwapDate(ownOverride.startDate)
        : start;
      const peerOverride = existing.channelSchedules?.[channelId];
      const peerStart = peerOverride
        ? toBannerSwapDate(peerOverride.startDate)
        : toBannerSwapDate(existing.startDate);
      const peerEnd = peerOverride
        ? toBannerSwapDate(peerOverride.endDate)
        : toBannerSwapDate(existing.endDate);
      if (!isValid(ownStart) || !isValid(peerStart) || !isValid(peerEnd)) {
        continue;
      }
      // Half-open [start, end): touching endpoints do not overlap (matches server).
      if (ownStart.getTime() < peerEnd.getTime() && peerStart.getTime() < e) {
        overlaps.push(existing.swapUID);
        break;
      }
    }
  }
  return overlaps;
}

export function channelResultsSummary(
  channelResults: BannerSwap["channelResults"]
): string | null {
  if (!channelResults?.length) {
    return null;
  }
  const terminal = new Set([
    "completed",
    "failed",
    "skipped",
  ]);
  const done = channelResults.filter((r) => terminal.has(r.status)).length;
  const failed = channelResults.filter((r) => r.status === "failed").length;
  const retrying = channelResults.filter((r) => r.status === "retrying").length;
  let suffix = "";
  if (failed > 0) {
    suffix = ` · ${failed} failed`;
  } else if (retrying > 0) {
    suffix = ` · ${retrying} retrying`;
  }
  return `${done}/${channelResults.length}${suffix}`;
}
